import React, { FC, useState } from 'react';
import {
    Alert,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { getIntegrationApiModesTemplate, setIntegrationApiModeTemplate } from 'apollo/templates/integration-switch';

type IntegrationApiMode = 'MOCK' | 'REAL';

type IntegrationMockServer = 'SCAN_CASE' | 'ACCOUNTING';

interface IntegrationVendorMode {
    vendor: string;
    mode: IntegrationApiMode;
    realConfigured: boolean;
}

interface IntegrationServerModes {
    server: IntegrationMockServer;
    vendors: IntegrationVendorMode[];
}

const SERVER_LABEL_KEYS: Record<IntegrationMockServer, string> = {
    SCAN_CASE: 'app-settings:INTEGRATION_API_MODE_SCAN_CASE',
    ACCOUNTING: 'app-settings:INTEGRATION_API_MODE_ACCOUNTING',
};

const IntegrationApiModeSettings: FC = () => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const [confirmTarget, setConfirmTarget] = useState<{
        server: IntegrationMockServer;
        mode: IntegrationApiMode;
    } | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // A failing mock control plane must not take the page down with it: the data set switch
    // above lives on the same tab (DD-3686). Show the error in this panel instead.
    const {
        data,
        loading,
        error: loadError,
        refetch,
    } = useQuery<{ integrationApiModes: IntegrationServerModes[] }>(getIntegrationApiModesTemplate, {
        notifyOnNetworkStatusChange: true,
    });

    const [setIntegrationApiMode, { loading: saving }] = useMutation(setIntegrationApiModeTemplate, {
        onCompleted: () => {
            setConfirmTarget(null);
            setErrorMessage(null);
            refetch();
        },
        onError: (error) => {
            setConfirmTarget(null);
            setErrorMessage(error.message);
        },
    });

    if (loadError && !loading) {
        return (
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h1" gutterBottom>
                    {translate('app-settings:INTEGRATION_API_MODE_TITLE')}
                </Typography>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            {translate('common:TRY_AGAIN')}
                        </Button>
                    }
                >
                    {translate('app-settings:INTEGRATION_API_MODE_LOAD_FAILED')}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {loadError.message}
                    </Typography>
                </Alert>
            </Paper>
        );
    }

    if (loading || !data) {
        return null;
    }

    const requestModeChange = (server: IntegrationMockServer, mode: IntegrationApiMode) => {
        // Switching TO the real vendor APIs deserves a confirmation; back to mock does not.
        if (mode === 'REAL') {
            setConfirmTarget({ server, mode });
            return;
        }
        setIntegrationApiMode({ variables: { server, mode } });
    };

    const isServerReal = (serverModes: IntegrationServerModes) =>
        serverModes.vendors.some((vendorMode) => vendorMode.mode === 'REAL');

    return (
        <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h1" gutterBottom>
                {translate('app-settings:INTEGRATION_API_MODE_TITLE')}
            </Typography>
            <Typography sx={{ mb: 3 }}>{translate('app-settings:INTEGRATION_API_MODE_DESCRIPTION')}</Typography>

            {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                </Alert>
            )}

            <Stack spacing={2}>
                {data.integrationApiModes.map((serverModes) => {
                    const anyReal = isServerReal(serverModes);
                    const realAvailable = serverModes.vendors.some((vendorMode) => vendorMode.realConfigured);
                    return (
                        <Paper key={serverModes.server} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                                <Typography sx={{ fontWeight: 'bold', minWidth: 180 }}>
                                    {translate(SERVER_LABEL_KEYS[serverModes.server])}
                                </Typography>
                                {serverModes.vendors.map((vendorMode) => (
                                    <Chip
                                        key={vendorMode.vendor}
                                        size="small"
                                        label={`${vendorMode.vendor}: ${vendorMode.mode === 'REAL' ? 'REAL TEST' : vendorMode.mode}`}
                                        color={vendorMode.mode === 'REAL' ? 'warning' : 'default'}
                                    />
                                ))}
                            </Stack>
                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                                <Button
                                    variant={anyReal ? 'text' : 'contained'}
                                    size="small"
                                    disabled={saving || !anyReal}
                                    onClick={() => requestModeChange(serverModes.server, 'MOCK')}
                                >
                                    {translate('app-settings:INTEGRATION_API_MODE_USE_MOCK')}
                                </Button>
                                <Button
                                    variant={anyReal ? 'contained' : 'text'}
                                    color="warning"
                                    size="small"
                                    disabled={saving || anyReal || !realAvailable}
                                    onClick={() => requestModeChange(serverModes.server, 'REAL')}
                                >
                                    {translate('app-settings:INTEGRATION_API_MODE_USE_REAL')}
                                </Button>
                            </Stack>
                            {!realAvailable && (
                                <Typography variant="caption" color="text.secondary">
                                    {translate('app-settings:INTEGRATION_API_MODE_REAL_UNAVAILABLE')}
                                </Typography>
                            )}
                        </Paper>
                    );
                })}
            </Stack>

            <Dialog open={Boolean(confirmTarget)} onClose={() => setConfirmTarget(null)}>
                <DialogTitle>{translate('app-settings:INTEGRATION_API_MODE_CONFIRM_TITLE')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{translate('app-settings:INTEGRATION_API_MODE_CONFIRM_BODY')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmTarget(null)}>{translate('common:CANCEL')}</Button>
                    <Button
                        color="warning"
                        variant="contained"
                        disabled={saving}
                        onClick={() =>
                            confirmTarget &&
                            setIntegrationApiMode({
                                variables: {
                                    server: confirmTarget.server,
                                    mode: confirmTarget.mode,
                                },
                            })
                        }
                    >
                        {translate('app-settings:INTEGRATION_API_MODE_CONFIRM_ACTION')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default IntegrationApiModeSettings;
